import type { Params } from 'react-router';
import type { Market } from '@/domain/side';

/** The two values a `:market` segment may take; anything else is the not-found page. */
export const markets: readonly Market[] = ['spot', 'usdm'];

export function isMarket(value: unknown): value is Market {
  return value === 'spot' || value === 'usdm';
}

export type ScreenId =
  | 'login'
  | 'logout'
  | 'home'
  | 'groups'
  | 'newGroup'
  | 'group'
  | 'symbols'
  | 'botOrders'
  | 'botOrder'
  | 'botHistory'
  | 'klines'
  | 'lines'
  | 'lineTrades'
  | 'p2p'
  | 'accounts'
  | 'blocks'
  | 'notFound';

export type Screen = {
  /** The page title in the header and in the browser tab ("Groups · Trader"). */
  title: string | ((params: Params) => string);
};

/** What a route's `handle` holds: which screen it shows. */
export type RouteHandle = { screen: ScreenId };

/** Every screen of the app, as data: its title. */
export const screens: Record<ScreenId, Screen> = {
  login: { title: 'Sign in' },
  logout: { title: 'Signing out' },
  home: { title: 'Home' },
  groups: { title: 'Groups' },
  newGroup: { title: 'New group' },
  group: { title: 'Group' },
  symbols: { title: 'Symbols' },
  botOrders: { title: 'Bot orders' },
  botOrder: { title: (params) => (params.symbol ? `${params.symbol} orders` : 'Order details') },
  botHistory: { title: 'Bot history' },
  klines: { title: 'Klines chart' },
  lines: { title: 'Line charts' },
  lineTrades: { title: 'Line trades' },
  p2p: { title: 'P2P calculator' },
  accounts: { title: 'Accounts' },
  blocks: { title: 'Building blocks' },
  notFound: { title: 'Not found' },
};

export function isScreenId(value: unknown): value is ScreenId {
  return typeof value === 'string' && Object.hasOwn(screens, value);
}

/** The screen a route handle names, or null for routes without one. */
export function screenOfHandle(handle: unknown): ScreenId | null {
  if (typeof handle !== 'object' || handle === null) return null;
  const screen = (handle as { screen?: unknown }).screen;
  return isScreenId(screen) ? screen : null;
}

export function screenTitle(id: ScreenId, params: Params): string {
  const { title } = screens[id];
  return typeof title === 'function' ? title(params) : title;
}
